import { supabase } from './supabase';

export interface User {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  role: 'user' | 'admin' | 'moderator';
  email_verified: boolean;
  created_at: string;
}

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: User;
}

class AuthService {
  private currentUser: User | null = null;
  private currentSession: AuthSession | null = null;

  async performAuraScan(data: any) {
    try {
      const { data: result, error } = await supabase.functions.invoke('auth-service', {
        body: { action: 'aura_scan', ...data }
      });

      if (error) throw error;
      return result;
    } catch (error) {
      console.error('Aura scan error:', error);
      throw new Error(`Aura scan failed: ${error.message}`);
    }
  }

  async checkHeartPurity(data: any) {
    try {
      const { data: result, error } = await supabase.functions.invoke('auth-service', {
        body: { action: 'heart_purity_check', ...data }
      });

      if (error) throw error;
      return result;
    } catch (error) {
      console.error('Heart purity check error:', error);
      throw new Error(`Heart purity check failed: ${error.message}`);
    }
  }

  async temporalAuthentication(temporalKey: string, dimensionalSignature: string) {
    try {
      const { data, error } = await supabase.functions.invoke('auth-service', {
        body: { action: 'temporal_authentication', temporalKey, dimensionalSignature }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Temporal authentication error:', error);
      throw new Error(`Temporal authentication failed: ${error.message}`);
    }
  }

  async verifyIntentions(intentions: string, purpose: string) {
    try {
      const { data, error } = await supabase.functions.invoke('auth-service', {
        body: { action: 'intention_verification', intentions, purpose }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Intention verification error:', error);
      throw new Error(`Intention verification failed: ${error.message}`);
    }
  }

  async signup(email: string, password: string, userData?: { full_name?: string }) {
    const { data, error } = await supabase.functions.invoke('auth-service', {
      body: { action: 'signup', email, password, userData }
    });

    if (error) throw error;
    return data;
  }

  async login(email: string, password: string) {
    const { data, error } = await supabase.functions.invoke('auth-service', {
      body: { action: 'login', email, password }
    });

    if (error) throw error;
    
    this.currentUser = data.user;
    this.currentSession = data.session;
    
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    localStorage.setItem('auth_session', JSON.stringify(data.session));
    
    return data;
  }

  async logout() {
    await supabase.auth.signOut();
    this.currentUser = null;
    this.currentSession = null;
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_session');
  }

  getCurrentUser(): User | null {
    if (this.currentUser) return this.currentUser;
    
    const stored = localStorage.getItem('auth_user');
    if (stored) {
      this.currentUser = JSON.parse(stored);
      return this.currentUser;
    }
    
    return null;
  }

  getCurrentSession(): AuthSession | null {
    if (this.currentSession) return this.currentSession;
    
    const stored = localStorage.getItem('auth_session');
    if (stored) {
      this.currentSession = JSON.parse(stored);
      return this.currentSession;
    }
    
    return null;
  }

  isAuthenticated(): boolean {
    const session = this.getCurrentSession();
    if (!session) return false;
    
    return Date.now() < session.expires_at * 1000;
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    if (user.role === 'admin') return true;
    
    const permissions = {
      user: ['read', 'write_own'],
      moderator: ['read', 'write_own', 'moderate'],
      admin: ['read', 'write', 'delete', 'moderate', 'admin']
    };
    
    return permissions[user.role]?.includes(permission) || false;
  }
}

export const authService = new AuthService();
export { AuthService };